import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { TagWithChildren } from '@eview/core/domain/tags/tag.helpers';
import { Actions, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Subscription, Observable } from 'rxjs';
import { selectTags } from '@eview/core/store/selectors/tags.selector';
import { AppState } from '@eview/core/store/states/app.state';
import { TagHelpers } from '@eview/core/domain/tags/tag.helpers';
import { SearchableSelectOption } from '@eview/features/ui/searchable-select/searchable-select.component';
import { CatalogFacade } from '@eview/core/store/facades/catalog.facade';
@Component({
  selector: 'eview-tag-selector',
  templateUrl: 'tag-selector.component.html',
  styleUrls: ['tag-selector.component.scss']
})
export class TagSelectorComponent implements OnInit, OnChanges, OnDestroy {
  constructor(
    private store: Store<AppState>,
    private actions$: Actions,
    private catalog: CatalogFacade
  ) {
    this.subs = new Subscription();
    this.tags$ = this.store.select(selectTags);
  }

  ngOnInit() {
    this.catalog.ensureTags();

    this.subs.add(
      this.tags$.subscribe((categories) => {
        this.cats = categories?.results ?? [];
      })
    );
    
    this.subs.add(
      this.actions$
        .pipe(ofType(ETagSelectorActions.Touched))
        .subscribe(() => {
          this.touched = true;
        })
    );
    this.subs.add(
      this.actions$
        .pipe(ofType<TagSelectorSetById>(ETagSelectorActions.SetById))
        .subscribe(action => {
          const { id, type } = this.normalizeSetByIdPayload(action.payload);
          // Ignore SetById meant for another taxonomy (edit form has many tag fields).
          if (type && this.taxonomyKey && type !== this.taxonomyKey) {
            return;
          }
          this.pendingSetById = id;
          this.applySelectionById(id);
        })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['nestedTags']) {
      return;
    }
    // Re-apply saved selection when filtered options arrive late (edit mode).
    if (this.pendingSetById != null) {
      this.applySelectionById(this.pendingSetById);
      return;
    }
    if (changes['nestedTags'].firstChange) {
      return;
    }
    // Only clear when the current value is no longer a valid option.
    if (this.tag == null) {
      return;
    }
    const stillPresent = (this.nestedTags || []).some(
      item => Number(item.id) === Number(this.tag)
    );
    if (!stillPresent) {
      this.tag = null;
    }
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
    this.tag = null;
  }

  @Input() public nestedTags: TagWithChildren[];
  @Input() public selectedVal: any[];
  @Input() public isRequired: boolean = false;
  /** Scopes SetById to this field's taxonomy (avoids cross-field corruption on edit). */
  @Input() public taxonomyKey: string;

  tag: (TagWithChildren | any) = null;
  touched: boolean = false;
  private pendingSetById: number | null = null;

  get tagSelectOptions(): SearchableSelectOption[] {
    return (this.nestedTags || []).map(t => ({
      value: t.id,
      label: t.tag
    }));
  }

  private subs: Subscription;

  cats: any [];

  tags$: Observable<any>;

  private normalizeSetByIdPayload(
    payload: number | { id: number; type?: string }
  ): { id: number; type?: string } {
    if (payload != null && typeof payload === 'object') {
      return { id: Number(payload.id), type: payload.type };
    }
    return { id: Number(payload) };
  }

  private applySelectionById(id: number | null | undefined): void {
    if (id == null || id === -1 || Number.isNaN(Number(id))) {
      return;
    }
    if (!this.nestedTags?.length) {
      // Options not ready yet (e.g. filter_by_parent); keep pending.
      this.pendingSetById = id;
      return;
    }
    const match = this.nestedTags.find(
      item => Number(item.id) === Number(id)
    );
    if (match) {
      this.selectedVal = [match];
      this.tag = match.id;
      this.pendingSetById = null;
      setTimeout(() => {
        this.store.dispatch(new TagSelectorChange(match));
      });
      return;
    }
    // Id is not an option in this selector (often another field's SetById).
    // Do not fall back to "first child" — that corrupted sibling taxonomies.
    this.pendingSetById = null;
  }

  onChange() {
    const tagId = this.tag;
    this.pendingSetById = null;
    // Only same-taxonomy children (from the rendered tree) make a tag a
    // non-selectable group; cross-taxonomy children (e.g. ODPE → locales,
    // linked via tags.parent_id) must not block selection.
    const selected = (this.nestedTags || []).find(item => item.id === tagId);
    if (selected?.children?.length > 0) {
      this.store.dispatch(
        new TagSelectorChange({
          type: this.nestedTags?.[0]?.type,
          id: null,
          val: null
        } as TagWithChildren)
      );
      return;
    }
    if (this.cats) {
      const newTag = this.cats.filter(item => item.id === tagId);
      const catVal =
        newTag.length > 0
          ? newTag[0]
          : { type: this.nestedTags[0].type, val: null };
      this.store.dispatch(new TagSelectorChange(catVal));
    }
  }

  markParentAsTouched() {
    this.store.dispatch(new TagSelectorTouched());
  }

  markAsTouched() {
    this.touched = true;
  }
}

export interface TagSelectorState {
  tag: TagWithChildren;
}

export enum ETagSelectorActions {
  Change = '[TagSelector] Change',
  SetById = '[TagSelector] Set by Id',
  Touched = '[TagSelector] Touched'
}

export class TagSelectorChange implements Action {
  readonly type = ETagSelectorActions.Change;
  constructor(public payload: TagWithChildren) {}
}

export class TagSelectorSetById implements Action {
  readonly type = ETagSelectorActions.SetById;
  /** Bare id (legacy) or `{ id, type }` scoped to a tag taxonomy key. */
  constructor(public payload: number | { id: number; type?: string }) {}
}

export class TagSelectorTouched implements Action {
  readonly type = ETagSelectorActions.Touched;
}
